<template>
  <!-- プレビュー画面 -->
  <div v-if="photostream">
    <!-- PDF変換が失敗した場合 -->
    <div
      v-if="isFailure"
      class="default-tmb position-relative d-flex flex-column justify-content-center align-items-center"
    >
      <div class="w-100 h-100 d-flex justify-content-center align-items-center">
        <img loading="lazy" :src="officeThumnail" class="preview-icon-size" @load="onLoadImage" />
      </div>
      <div>{{ NO_PREVIEW_MESSAGE }}</div>
    </div>
    <!-- ローディング表示 -->
    <loading-attachment v-if="isLoading" :fullscreen="false" :msg="LoadingMessage" />
    <!-- PDF表示 -->
    <div v-if="isSuccess" class="pdf-previewer">
      <preview-pdf-view
        class="pdf-viewer"
        :id="id"
        :file="pdfUrl"
        :isMobile="isMobile"
        @load="onLoadImage"
        :saveAllowed="downloadable"
      />
    </div>
  </div>
  <!-- 一覧画面の添付ファイル -->
  <div v-else class="office-grey-box">
    <div class="my-3 px-3 d-flex flex-column justify-content-center align-items-center">
      <div class="loaded-tmb">
        <img loading="lazy" class="list-icon-size" :src="officeThumnail" @load="onLoadImage" />
      </div>
      <div class="file-name">{{ imageFileName }}</div>
    </div>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import { useAppStore } from "@/store";
import LoadingAttachment from "../loading/LoadingAttachment.vue";
import PdfConverterUtility, {
  PdfConverterParam,
  PdfConverterStatus,
} from "@/components/pdf-converter-utility";
import type { Attachment } from "@/model";
import PreviewPdfView from "../preview_pdf/PreviewPdfView.vue";
import { getFileLocalUrl, isMacroEnabled } from "../file-utility";
import path from "path";

const NO_PREVIEW_MESSAGE = "このファイルはプレビューできません";
const GENERATE_PREVIEW_FILE_MESSAGE =
  "サーバーでプレビューファイルを生成しています。しばらくお待ちください。";

const POLLING_TIME = 3 * 1000; // 3秒

// NOTE: 旧コードでは CustomButton も登録されていたが、テンプレート未使用の
// デッド登録だったため除去（振る舞い不変）。
export default defineComponent({
  components: {
    LoadingAttachment,
    PreviewPdfView,
  },
  props: {
    photostream: { type: Boolean, default: false },
    officeThumnail: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    imageFileName: { type: String, default: "" },
    allowDownload: { type: Boolean, default: false },
    id: { type: String, default: "" },
    isMobile: { type: Boolean, default: false },
    downloadable: { type: Boolean, default: false },
    attachment: { type: Object as PropType<Attachment>, default: undefined },
    param: {
      type: Object as PropType<{ messageId: string; commentId: string }>,
      // NOTE: 旧コードはオブジェクトリテラル直書きの default（Vue 2 では dev 警告対象）
      // だったが、eslint(vue/require-valid-default-prop) 対応で factory 化。
      // param は読み取り専用のため挙動は変わらない。
      default: () => ({ messageId: "", commentId: "" }),
    },
    downloadFile: { type: Function as PropType<() => void>, default: Function },
    onLoadImage: { type: Function as PropType<() => void>, default: Function },
  },
  setup(props) {
    // PDF変換ステータス
    const pdfConverterStatus = ref<PdfConverterStatus>("LOADING");
    // PDFのURL
    const pdfUrl = ref<string>("");
    // ポーリングタイマー
    const pollingTimer = ref<number | null>(null);

    // ロード中のメッセージ
    const LoadingMessage = computed(() => {
      if (pdfConverterStatus.value === "PENDING" || pdfConverterStatus.value === "PROCESSING") {
        // PDF変換中のメッセージ
        return GENERATE_PREVIEW_FILE_MESSAGE;
      }
      return "Now Loading...";
    });

    const pdfConverterParam = computed((): PdfConverterParam => {
      return {
        domainId: useAppStore().domainId,
        topicId: useAppStore().topicId,
        fileName: props.attachment.url,
        ...props.param,
      };
    });

    const isSuccess = computed((): boolean => {
      return pdfConverterStatus.value === "SUCCESS";
    });

    const isLoading = computed((): boolean => {
      return (
        pdfConverterStatus.value === "LOADING" ||
        pdfConverterStatus.value === "PENDING" ||
        pdfConverterStatus.value === "PROCESSING" ||
        pdfConverterStatus.value === "EMPTY"
      );
    });

    const isFailure = computed((): boolean => {
      return pdfConverterStatus.value === "FAILURE";
    });

    // PDF変換のステータスを取得
    const getPdfConverterStatus = async (): Promise<PdfConverterStatus | null> => {
      const pdfConverterUtility = PdfConverterUtility.getInstance();
      const res = await pdfConverterUtility.getPdfConverterStatus(pdfConverterParam.value);
      return res?.pdfConverterStatus ?? null;
    };

    // PDF変換を開始
    const startPdfConversion = async (): Promise<void> => {
      const pdfConverterUtility = PdfConverterUtility.getInstance();
      await pdfConverterUtility.sendStartPdfConversion(pdfConverterParam.value);
    };

    // PDFのURLを取得
    const getPdfUrl = async (): Promise<string> => {
      const pdfConverterUtility = PdfConverterUtility.getInstance();
      const response = await pdfConverterUtility.getPdfAttachmentFile(pdfConverterParam.value);
      if (response?.fileUrl) {
        const fileName = path.basename(response.fileUrl.split("?")[0]);
        const url = await getFileLocalUrl(response.fileUrl, fileName);
        return url;
      } else {
        return "";
      }
    };

    const handlePdfStatusChange = async (newStatus: PdfConverterStatus) => {
      switch (newStatus) {
        case "PENDING":
        case "PROCESSING":
          break;
        case "SUCCESS":
          pdfUrl.value = await getPdfUrl();
          stopPolling();
          break;
        case "FAILURE":
          stopPolling();
          break;
      }
      pdfConverterStatus.value = newStatus;
    };

    const processPolling = async (): Promise<void> => {
      const statusData = await getPdfConverterStatus();
      if (statusData) {
        await handlePdfStatusChange(statusData);
      }
    };

    // ポーリング開始
    const startPolling = (): void => {
      if (pollingTimer.value) {
        window.clearInterval(pollingTimer.value);
      }

      pollingTimer.value = window.setInterval(async () => {
        await processPolling();
      }, POLLING_TIME);
    };

    // ポーリング停止
    const stopPolling = (): void => {
      if (pollingTimer.value) {
        window.clearInterval(pollingTimer.value);
        pollingTimer.value = null;
      }
    };

    // 初期化処理
    const initializePdfConversion = async (): Promise<void> => {
      if (!props.photostream) return;

      // 初期化
      stopPolling();
      pdfConverterStatus.value = "LOADING";
      clearPdfUrl();

      // マクロ入りファイルはプレビューしない
      if (isMacroEnabled(props.attachment.mime)) {
        pdfConverterStatus.value = "FAILURE";
        return;
      }

      try {
        const statusData = await getPdfConverterStatus();

        switch (statusData) {
          case "PENDING":
          case "PROCESSING":
            pdfConverterStatus.value = statusData;
            startPolling();
            break;
          case "SUCCESS":
            pdfUrl.value = await getPdfUrl();
            if (pdfUrl.value) {
              pdfConverterStatus.value = "SUCCESS";
            } else {
              pdfConverterStatus.value = "LOADING";
              await startPdfConversion();
              startPolling();
            }
            break;
          default:
            await startPdfConversion();
            pdfConverterStatus.value = "PENDING";
            startPolling();
            break;
        }
      } catch (error) {
        pdfConverterStatus.value = "FAILURE";
        stopPolling();
      }
    };

    const clearPdfUrl = (): void => {
      if (pdfUrl.value) {
        URL.revokeObjectURL(pdfUrl.value);
        pdfUrl.value = "";
      }
    };

    // プレビューするファイルが変わった時に発火
    watch(
      () => props.attachment,
      async () => {
        initializePdfConversion();
      }
    );

    onMounted(() => {
      initializePdfConversion();
    });

    onBeforeUnmount(() => {
      stopPolling();
      clearPdfUrl();
    });

    return {
      pdfConverterStatus,
      pdfUrl,
      pollingTimer,
      // PDF変換が失敗した場合のメッセージ
      // NOTE: 旧 getter はモジュール定数を返すだけのため、定数をそのまま公開する
      NO_PREVIEW_MESSAGE,
      LoadingMessage,
      pdfConverterParam,
      isSuccess,
      isLoading,
      isFailure,
      getPdfConverterStatus,
      startPdfConversion,
      getPdfUrl,
      handlePdfStatusChange,
      processPolling,
      startPolling,
      stopPolling,
      initializePdfConversion,
      clearPdfUrl,
    };
  },
});
</script>

<style lang="scss" scoped>
.office-grey-box {
  width: 100%;
}
.default-tmb {
  display: grid;
  justify-items: center;
  align-items: center;
  width: 320px;
  height: 320px;
  color: #212529;
  margin-bottom: 8px;
  border-radius: 8px;
  position: relative;
  background-color: white;
}
.loaded-tmb {
  position: relative;
  margin-bottom: 8px;
}
.preview-icon-size {
  height: 96px;
}
.list-icon-size {
  height: 72px;
  width: 60px;
}
.file-name {
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  text-align: center;
  color: #707070;
}
.pdf-previewer {
  height: 85vh;
  width: 85vw;
}
</style>
